import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { Button } from "../ui/button";
import { toast } from "sonner";
import { Camera, Loader2, X } from "lucide-react";

export const PhotoPicker = ({ files, setFiles, max, testId }) => {
  const [previews, setPreviews] = useState([]);
  useEffect(() => {
    const urls = files.map((f) => URL.createObjectURL(f));
    setPreviews(urls);
    return () => urls.forEach((u) => URL.revokeObjectURL(u));
  }, [files]);
  const add = (e) => {
    const picked = Array.from(e.target.files || []);
    e.target.value = "";
    if (files.length + picked.length > max) return toast.error(`You can add up to ${max} more photo${max === 1 ? "" : "s"}`);
    setFiles([...files, ...picked]);
  };
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-2">
        {previews.map((src, i) => (
          <div key={src} className="relative">
            <img src={src} alt="" className="w-20 h-20 rounded-lg object-cover border border-slate-200" />
            <button type="button" onClick={() => setFiles(files.filter((_, x) => x !== i))} aria-label="Remove" data-testid={`${testId}-preview-remove-${i}`}
              className="absolute -top-1.5 -right-1.5 bg-white rounded-full shadow p-0.5 text-red-600"><X className="w-3.5 h-3.5" /></button>
          </div>
        ))}
        {files.length < max && (
          <label className="w-20 h-20 rounded-lg border-2 border-dashed border-amber-300 flex flex-col items-center justify-center text-amber-700 text-xs cursor-pointer hover:bg-amber-50" data-testid={testId}>
            <Camera className="w-5 h-5" />Add
            <input type="file" accept="image/*" multiple className="hidden" onChange={add} data-testid={`${testId}-input`} />
          </label>
        )}
      </div>
    </div>
  );
};

export const ContractorCompleteDialog = ({ open, onOpenChange, remaining, onConfirm }) => {
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setFiles([]); }, [open]);
  const confirm = async () => {
    setBusy(true);
    try { await onConfirm(files); onOpenChange(false); } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm" data-testid="contractor-complete-dialog">
        <DialogHeader className="text-left">
          <DialogTitle>Mark as Complete</DialogTitle>
          <DialogDescription>Add “after” photos of the finished work (optional, up to {remaining}).</DialogDescription>
        </DialogHeader>
        {remaining > 0 ? <PhotoPicker files={files} setFiles={setFiles} max={remaining} testId="contractor-complete-add-photo" />
          : <p className="text-sm text-slate-500">This defect already has the maximum of 3 completion photos.</p>}
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="contractor-complete-cancel">Cancel</Button>
          <Button onClick={confirm} disabled={busy} className="bg-green-600 hover:bg-green-700 text-white" data-testid="contractor-complete-confirm">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : files.length ? `Upload & Mark Complete` : "Mark Complete"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
